import type {
  CrossroadsLog,
  GroupMetadata,
} from '@/api/hooks/useIntegrationLogs'
import { Vertical } from '@gravitate-js/excalibrr'
import type { TabsProps } from 'antd'
import { Tabs } from 'antd'
import { useMemo } from 'react'
import { DebugTabContent } from './DebugTabContent'
import {
  AuthenticationFailureCard,
  LogDetailStatusCard,
  MappingFailureCard,
} from './DetailCards'
import {
  BasicInformationCard,
  RecordInformationCard,
  isOrderChangeMetadata,
} from './LogInfoBoxes'
import { OrderChangeSummaryCard } from './OrderChangeSummary'

export type LegacyLogDetailsPanelProps = {
  log: CrossroadsLog
  groupMetadata?: GroupMetadata | null
}

const CONTAINER_STYLE = { flex: 2, width: '100%', overflowY: 'scroll' as const }

function LogDetailCard({ log }: { log: CrossroadsLog }) {
  const category = log.detail?.category as string | undefined
  if (category === 'mapping') {
    return <MappingFailureCard log={log} />
  }
  if (category === 'authentication') {
    return <AuthenticationFailureCard log={log} />
  }
  return <LogDetailStatusCard log={log} />
}

export function LegacyLogDetailsPanel({
  log,
  groupMetadata,
}: LegacyLogDetailsPanelProps) {
  const tabItems: TabsProps['items'] = useMemo(() => {
    return [
      {
        key: 'details',
        label: 'Details',
        children: (
          <Vertical className='mt-4'>
            <LogDetailCard log={log} />
            {isOrderChangeMetadata(log.metadata) && (
              <OrderChangeSummaryCard
                beforeChange={log.metadata.before_change}
                afterChange={log.metadata.after_change}
                timestamp={log.date}
              />
            )}
            <BasicInformationCard log={log} />
            <RecordInformationCard log={log} groupMetadata={groupMetadata} />
          </Vertical>
        ),
      },
      {
        key: 'debug',
        label: 'Debug / Developer Info',
        children: <DebugTabContent log={log} />,
      },
    ]
  }, [log, groupMetadata])

  return (
    <div style={CONTAINER_STYLE}>
      <Tabs defaultActiveKey='details' size='large' items={tabItems} />
    </div>
  )
}
